// Situation et délais d'un dossier : état, statut usager, échéances interne et réglementaire,
// suspension en cours, réouvertures, version de la démarche et migration individuelle (droit de
// valider la configuration) vers la version publiée plus récente, motivée et journalisée.
import type { Demarche } from '@cc/contrats';
import type { DossierDetail } from '@cc/contrats/ui-metier';
import { Lien, Panneau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../../api/client';
import { date } from '../../../commun/format';
import { useSession } from '../../../session/FournisseurSession';
import { peut } from '../../../session/permissions';
import { ActionMetier } from '../ActionMetier';
import { libelleEtatDossier } from '../listes';
import { puceDossier } from '../PageDossiers';
import commun from '../../../commun/commun.module.css';

function retardEnJours(echeance: string | null): number {
  if (!echeance) return 0;
  const e = new Date(`${echeance}T00:00:00`).getTime();
  return Math.max(0, Math.floor((Date.now() - e) / 86_400_000));
}

export function EcheancesDossier({ dossier: d, invalider }: { dossier: DossierDetail; invalider: (readonly unknown[])[] }) {
  const { contexte } = useSession();
  const demarche = useQuery({
    queryKey: ['demarches', 'fiche', d.demarche.id],
    queryFn: ({ signal }) => requete<Demarche>(`/demarches/${d.demarche.id}`, { signal }),
    retry: false,
    staleTime: 60_000,
  });
  const courante = demarche.data?.version_courante;
  const migrable = Boolean(courante && courante.numero > d.demarche.numero_version && !['cloture', 'archive', 'classe_sans_suite', 'retire'].includes(d.etat));
  const retardInterne = d.en_retard ? retardEnJours(d.echeance_interne) : 0;
  return (
    <Panneau titre="Situation et délais">
      <dl className={commun.definitions}>
        <dt>État</dt>
        <dd>
          {puceDossier(d)} <span className={commun.na}>({libelleEtatDossier(d.etat)})</span>
        </dd>
        <dt>Statut vu par l&apos;usager</dt>
        <dd>{d.statut_usager_libelle}</dd>
        <dt>Enregistré le</dt>
        <dd>
          {date(d.enregistre_le)} ({d.canal_depot})
        </dd>
        <dt>Échéance interne</dt>
        <dd>
          {date(d.echeance_interne)}
          {d.en_retard ? <strong style={{ color: 'var(--cc-retard)' }}> , en retard de {retardInterne} jour{retardInterne > 1 ? 's' : ''}</strong> : null}
        </dd>
        <dt>Échéance réglementaire</dt>
        <dd>
          {date(d.echeance_reglementaire)}
          {d.hors_delai_reglementaire ? <strong style={{ color: 'var(--cc-retard)' }}> , dépassée</strong> : null}
        </dd>
        <dt>Suspension</dt>
        <dd>{d.suspendu ? 'Délais suspendus (attente d\'un complément) ; ils reprendront, prolongés de la durée de suspension, à la réception' : 'Aucune suspension en cours'}</dd>
        <dt>Réouvertures</dt>
        <dd>{d.nb_reouvertures}</dd>
        <dt>Version de la démarche</dt>
        <dd>
          <Lien href={`/demarches/${d.demarche.id}`}>{d.demarche.libelle}</Lien>, version {d.demarche.numero_version}
          {courante && courante.numero !== d.demarche.numero_version ? <span className={commun.na}> (version {courante.numero} publiée depuis : ce dossier garde les règles de sa version)</span> : null}
        </dd>
      </dl>
      {migrable && courante && peut(contexte, 'demarche_config', 'valider') ? (
        <div style={{ marginTop: 'var(--cc-esp-1)' }}>
          <ActionMetier
            libelle={`Migrer vers la version ${courante.numero}`}
            titre={`Migrer le dossier vers la version ${courante.numero}`}
            explication="La migration est refusée si une étape déjà franchie n'existe plus dans la version cible ; les écarts de formulaire et de pièces sont signalés. Elle est inscrite dans l'historique du dossier et dans le journal des migrations."
            chemin={`/dossiers/${d.id}/migrer`}
            corps={{ version_id: courante.id }}
            champs={[{ nom: 'motif', libelle: 'Motif de la migration', obligatoire: true, multiligne: true }]}
            succes={`Dossier migré vers la version ${courante.numero}.`}
            invalider={invalider}
            variante="secondaire"
          />
        </div>
      ) : null}
    </Panneau>
  );
}
